import { sort, SORT_DATA_TYPES } from './sort';
import { groupBy } from 'utils/array';
import { sortByKey } from 'utils/object';
import { LeftRight } from 'constants.js';

export const groupTests = testsList => {
  const groupedTests = groupBy(testsList, ['category']);
  for (const [groupId, tests] of Object.entries(groupedTests)) {
    groupedTests[groupId] = sort(tests, { keys: [{ key: 'title' }] });
  }
  return sortByKey(groupedTests);
};

export const groupTestResults = testResultList => {
  const groupedTestResults = groupBy(testResultList, ['category']);
  for (const [groupId, tests] of Object.entries(groupedTestResults)) {
    groupedTestResults[groupId] = sort(tests, {
      keys: [
        {
          key: 'testTitle',
          dataType: SORT_DATA_TYPES.STRING
        },
        {
          key: 'order',
          dataType: SORT_DATA_TYPES.NUMBER
        }
      ]
    });
  }
  return sortByKey(groupedTestResults);
};

export const testItemHasValues = (test, sides) => {
  return (
    sides &&
    Object.keys(sides).some(key => {
      return test?.[key] && test[key].some(value => value !== '');
    })
  );
};

export const checkLateralityIsDisabled = (test, sides) => {
  const lateralitySides = Array.isArray(sides)
    ? sides
    : Object.keys(sides)
        .map(side => (sides[side] ? side : null))
        .filter(s => s !== 'center' && s !== null);

  switch (test.laterality) {
    case 'bilateral_independent':
      if (!sides) return [false, false];
      return [
        lateralitySides.length > 0 && !lateralitySides.includes(LeftRight.LEFT),
        lateralitySides.length > 0 && !lateralitySides.includes(LeftRight.RIGHT)
      ];
    case 'bilateral_dependent':
      return [false, false];
    case 'unilateral':
    default:
      return [false];
  }
};

export const checkLateralityIsHidden = (testResult, sides, person) => {
  const onlyLeft = sides?.length > 0 && !sides.includes(LeftRight.RIGHT);
  const onlyRight = sides?.length > 0 && !sides.includes(LeftRight.LEFT);

  // Op Preventie
  const onlyDominantLeft =
    testResult.dominant_only && person.dominantHand !== LeftRight.RIGHT;
  const onlyDominantRight =
    testResult.dominant_only && person.dominantHand !== LeftRight.LEFT;

  switch (testResult.laterality) {
    case 'bilateral_independent':
      return [onlyRight || onlyDominantRight, onlyLeft || onlyDominantLeft];
    case 'bilateral_dependent':
      return [onlyRight || onlyDominantRight, onlyLeft || onlyDominantLeft];
    case 'unilateral':
    default:
      return [false];
  }
};

export const getTestSetGroups = testSets => {
  const sortedTestSets = sort(testSets, {
    keys: [
      { key: 'title' },
      { key: 'version', desc: true, dataType: SORT_DATA_TYPES.NUMBER }
    ]
  });
  return getRawGroups(sortedTestSets);
};

export const getRawGroups = tests => {
  const rawGroups = tests.reduce((accum, testSet) => {
    const groupLabel = testSet.id === testSet.originalId ? testSet.title : '';
    const group = accum.find(a => a.id === testSet.originalId);
    if (group) {
      group.label = group.label || groupLabel;
      group.children.push(testSet);
      return accum;
    }
    return [
      ...accum,
      {
        id: testSet.originalId,
        label: groupLabel || '',
        children: [testSet]
      }
    ];
  }, []);

  const sortedGroups = sort(rawGroups, {
    keys: [{ key: 'label' }]
  });

  return sortedGroups.map(sg => {
    sg.children = sort(sg.children, {
      keys: [
        {
          key: 'version',
          desc: true,
          dataType: SORT_DATA_TYPES.NUMBER
        }
      ]
    });
    return sg;
  });
};

export const getUniqueTestItems = tests => {
  const testItems = tests.reduce((tAccum, test) => {
    if (!test.testItems) {
      return tAccum.concat([]);
    }
    return tAccum.concat(
      test.testItems.reduce((tiAccum, ti) => {
        tAccum.findIndex(tia => tia.id === ti.id) === -1 && tiAccum.push(ti);
        return tiAccum;
      }, [])
    );
  }, []);
  return sort(
    testItems.filter(ti => !ti.hidden),
    { keys: [{ key: 'title' }] }
  );
};
